/**
 * La ficha abierta desde un toque (encargo 49): en el significado que el
 * selector eligió para esa frase, o en la palabra con la aclaración de que no
 * se pudo elegir. Es la ficha ÚNICA (`WordSheetLib`), así que se ven todos los
 * significados y se puede cambiar de uno a otro.
 *
 * Dentro, la definición y los ejemplos también se tocan: cada ficha que se abre
 * así se apila con «Volver a …», hasta `MAX_SHEET_DEPTH` contando las de abajo.
 * La cruz cierra todas.
 *
 * Desde la charla va en un portal (`portal`): la burbuja del coach tiene
 * `content-visibility`, que recortaría un overlay fijo adentro.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ContextMeaningPublicConfig } from '@orborix/shared-types';
import { deleteVocabByText } from '../../lib/englishApi.js';
import { saveCatalogWord } from './libApi.js';
import { libFromLexicon, type LibWord } from './libData.js';
import { MAX_SHEET_DEPTH, type ContextMeaningOpen } from './SheetTap.js';
import { WordSheetLib } from './WordSheetLib.js';
import './library.css';

export type { ContextMeaningOpen } from './SheetTap.js';

export function ContextMeaningSheet({
  initial,
  config,
  usage,
  outerBack,
  depthBelow = 0,
  onClose,
  portal = false,
  sessionId,
}: {
  initial: ContextMeaningOpen;
  config: ContextMeaningPublicConfig;
  /** La oración donde se tocó (sólo la primera ficha la muestra). */
  usage?: { label: string; node: ReactNode };
  /** «Volver» de la primera ficha, cuando hay otra debajo (la de la Biblioteca). */
  outerBack?: { label: string; onBack: () => void };
  /** Cuántas fichas hay abiertas debajo de ésta (para el tope). */
  depthBelow?: number;
  onClose: () => void;
  portal?: boolean;
  sessionId?: string;
}) {
  const [stack, setStack] = useState<ContextMeaningOpen[]>([initial]);
  // Guardar/quitar desde estas fichas: lo que cambió el alumno, por palabra.
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const hostRef = useRef<HTMLDivElement | null>(null);

  // Al apilar o volver, el foco va a la ficha de arriba (su «Volver»).
  useEffect(() => {
    if (stack.length < 2 && !outerBack) return;
    const id = requestAnimationFrame(() =>
      hostRef.current?.querySelector<HTMLElement>('.bf-back')?.focus(),
    );
    return () => cancelAnimationFrame(id);
  }, [stack.length, outerBack]);

  const top = stack[stack.length - 1]!;
  const base = libFromLexicon(top.entry);
  const w: LibWord = { ...base, saved: saved[base.text] ?? base.saved };

  const toggleSave = (cur: LibWord): void => {
    const next = !cur.saved;
    setSaved((p) => ({ ...p, [cur.text]: next }));
    const revert = () => setSaved((p) => ({ ...p, [cur.text]: !next }));
    if (!next) {
      void deleteVocabByText({ text: cur.text }).catch(revert);
      return;
    }
    // Lo que se guarda es el principal, como desde cualquier ficha (decisión 73).
    void saveCatalogWord({
      word: cur.text,
      ...(cur.tr ? { translation: cur.tr } : {}),
      ...(cur.ipa ? { ipa: cur.ipa } : {}),
      ...(cur.pos ? { partOfSpeech: cur.pos } : {}),
      ...(cur.cefr ? { cefr: cur.cefr } : {}),
      ...(cur.example ? { example: cur.example } : {}),
    }).catch(revert);
  };

  const below = stack[stack.length - 2];
  const back = below
    ? { label: below.entry.text, onBack: () => setStack((s) => s.slice(0, -1)) }
    : outerBack;

  const sheet = (
    <div ref={hostRef} className="bcm-top">
      <WordSheetLib
        key={`${stack.length}|${w.id}`}
        w={w}
        {...(top.meaningId ? { initialMeaningId: top.meaningId } : {})}
        contextNote={top.note}
        {...(back ? { back } : {})}
        onClose={onClose}
        onToggleSave={toggleSave}
        {...(usage && stack.length === 1 ? { usage } : {})}
        tapLookup={{
          config,
          canOpen: depthBelow + stack.length < MAX_SHEET_DEPTH,
          onOpen: (o) => setStack((s) => [...s, o]),
          ...(sessionId ? { sessionId } : {}),
        }}
      />
    </div>
  );
  return portal
    ? createPortal(<div className="bib-root bcm-host">{sheet}</div>, document.body)
    : sheet;
}
